import { Trans, useLingui } from "@lingui/react/macro";
import { Link } from "@tanstack/react-router";

import {
  Clause,
  LegalDocument,
  LegalSection,
  SELLER,
  SellerCard,
  ShortSummary,
  useSellerName,
} from "@/features/legal";

import { useProPlanLabels } from "../../lib/useProPlanLabels";
import { OFFER_REVISION } from "../../model/OFFER_REVISION";

const linkClassName = "text-accent hover:text-accent-strong";

// Пользовательское соглашение и публичная оферта (`/terms`) по макету
// design/prototype/Terms.dc.html. Бесплатная часть принимается входом через Steam, Pro — оплатой.
// Цена и срок — из `PRO_PLAN`, реквизиты — из `SELLER`.
export const PublicOffer = () => {
  const { t } = useLingui();
  const { price, period } = useProPlanLabels();
  const seller = useSellerName();
  const inn = SELLER.inn;
  const ogrnip = SELLER.ogrnip;
  const email = (
    <a href={`mailto:${SELLER.email}`} className={linkClassName}>
      {SELLER.email}
    </a>
  );
  const pricing = (
    <Link to="/pricing" className={linkClassName}>
      <Trans>Plans</Trans>
    </Link>
  );

  return (
    <LegalDocument title={t`User agreement and public offer`} revision={OFFER_REVISION}>
      <ShortSummary>
        <li>
          <Trans>
            Relock is free with a daily limit on analyses. Pro costs {price} for {period} and has no
            limit.
          </Trans>
        </li>
        <li>
          <Trans>
            Autopay is turned on only with your consent and can be turned off at any time.
          </Trans>
        </li>
        <li>
          <Trans>Changed your mind? We refund the unused days of Pro.</Trans>
        </li>
        <li>
          <Trans>An analysis is advice based on statistics. We don't guarantee wins.</Trans>
        </li>
      </ShortSummary>

      <LegalSection title={<Trans>1. General provisions</Trans>}>
        <Clause n="1.1">
          <Trans>
            This document is a public offer by {seller}, TIN {inn}, OGRNIP {ogrnip} (the
            “Provider”), addressed to any person (the “User”), to conclude an agreement on the terms
            below (Art. 435 and Art. 437(2) of the Civil Code of the Russian Federation).
          </Trans>
        </Clause>
        <Clause n="1.2">
          <Trans>
            The offer is accepted for free access to the Service by signing in with Steam, and for
            the Pro subscription by paying for it (Art. 438(3) of the Civil Code). Acceptance means
            full and unconditional agreement to the terms of the offer.
          </Trans>
        </Clause>
        <Clause n="1.3">
          <Trans>The current version of the offer is always available at relock.gg/terms.</Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>2. Terms</Trans>}>
        <Clause n="2.1">
          <Trans>
            <b>Service</b> means the Relock web service at relock.gg for Deadlock players.
          </Trans>
        </Clause>
        <Clause n="2.2">
          <Trans>
            <b>Account</b> means the User's account in the Service, created when signing in with
            Steam.
          </Trans>
        </Clause>
        <Clause n="2.3">
          <Trans>
            <b>Analysis</b> means the result of an automated analysis of a User's match: the
            mistakes found and the comparison with strong players on the same hero.
          </Trans>
        </Clause>
        <Clause n="2.4">
          <Trans>
            <b>Pro subscription</b> means paid access to the features in clause 3.2 for a billing
            period.
          </Trans>
        </Clause>
        <Clause n="2.5">
          <Trans>
            <b>Billing period</b> means {period} from the moment the payment is received.
          </Trans>
        </Clause>
        <Clause n="2.6">
          <Trans>
            <b>Autopay</b> means a regular charge for the next billing period via the Faster
            Payments System (SBP) with the User's consent.
          </Trans>
        </Clause>
        <Clause n="2.7">
          <Trans>
            <b>Payment service</b> means Platega, through which the Provider accepts payments.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>3. Subject</Trans>}>
        <Clause n="3.1">
          <Trans>
            The Provider gives the User free access to the Service: match analyses within the daily
            limit, drills based on mistakes, play style and comparison with friends. The limit is
            shown on the {pricing} page.
          </Trans>
        </Clause>
        <Clause n="3.2">
          <Trans>
            Under the Pro subscription, the Provider gives match analyses without the daily limit
            for the billing period.
          </Trans>
        </Clause>
        <Clause n="3.3">
          <Trans>
            New features the Provider adds to Pro are available to subscribers at no extra charge.
            During a paid billing period, Pro is not reduced.
          </Trans>
        </Clause>
        <Clause n="3.4">
          <Trans>
            An analysis is an information service: recommendations based on match statistics. The
            Provider does not guarantee a higher rank or more wins.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>4. How the services are provided</Trans>}>
        <Clause n="4.1">
          <Trans>
            The services are provided online in the User's Account at relock.gg. A Steam account is
            needed to sign in.
          </Trans>
        </Clause>
        <Clause n="4.2">
          <Trans>
            The Pro subscription turns on as soon as the payment is received. The billing period
            starts at that moment.
          </Trans>
        </Clause>
        <Clause n="4.3">
          <Trans>
            The Provider gets match data from the public statistics service deadlock-api. When the
            data appears and how accurate it is does not depend on the Provider.
          </Trans>
        </Clause>
        <Clause n="4.4">
          <Trans>
            The Service may be temporarily unavailable during updates and outages. The Provider
            strives to keep such breaks short.
          </Trans>
        </Clause>
        <Clause n="4.5">
          <Trans>
            The Service is not affiliated with Valve Corporation. Deadlock and Steam are trademarks
            of Valve Corporation.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>5. Prices and payment</Trans>}>
        <Clause n="5.1">
          <Trans>
            Prices are shown on the {pricing} page in rubles. The Pro subscription costs {price} per
            billing period.
          </Trans>
        </Clause>
        <Clause n="5.2">
          <Trans>
            Payment is accepted through the Payment service by SBP or bank card. The User enters
            card and account details on the Payment service's page; the Provider does not receive or
            store them.
          </Trans>
        </Clause>
        <Clause n="5.3">
          <Trans>
            The payment obligation is fulfilled when the Payment service confirms the payment.
          </Trans>
        </Clause>
        <Clause n="5.4">
          <Trans>
            An electronic receipt is issued for every payment and every refund. It is sent to the
            email address or phone number given at payment.
          </Trans>
        </Clause>
        <Clause n="5.5">
          <Trans>
            The Provider may change prices. The price of a billing period already paid for does not
            change. The Provider announces a new autopay price on the site at least 7 days before
            the charge; the User may turn autopay off before the charge.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>6. Autopay</Trans>}>
        <Clause n="6.1">
          <Trans>
            Autopay is turned on only with the User's separate consent: a checkbox on the payment
            page that is not ticked in advance, and a confirmation in the bank app via SBP.
          </Trans>
        </Clause>
        <Clause n="6.2">
          <Trans>
            With autopay on, {price} is charged at the start of each next billing period without
            additional confirmation. Each charge extends Pro by {period}.
          </Trans>
        </Clause>
        <Clause n="6.3">
          <Trans>
            After each charge, the Payment service sends the User an email with a link to turn
            autopay off.
          </Trans>
        </Clause>
        <Clause n="6.4">
          <Trans>
            The User may turn autopay off at any time: in the Account, in the bank app, via the link
            in the email or by writing to the Provider at {email}. After that there are no more
            charges, and Pro stays available until the end of the paid period.
          </Trans>
        </Clause>
        <Clause n="6.5">
          <Trans>
            If a charge fails, the Pro subscription is not extended until the next successful
            payment.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>7. Rights and obligations of the parties</Trans>}>
        <Clause n="7.1">
          <Trans>
            The Provider undertakes to provide access to the Service and to the paid Pro
            subscription, to announce changes to the terms as described in section 11 and to process
            the User's data under the{" "}
            <Link to="/privacy" className={linkClassName}>
              Privacy policy
            </Link>
            .
          </Trans>
        </Clause>
        <Clause n="7.2">
          <Trans>
            The Provider may develop and change the Service without reducing a paid Pro
            subscription, and may restrict the User's access if clause 7.3 is breached.
          </Trans>
        </Clause>
        <Clause n="7.3">
          <Trans>
            The User undertakes not to send automated requests to the Service, not to attempt
            unauthorized access, and not to resell or share access to the Account with third
            parties.
          </Trans>
        </Clause>
        <Clause n="7.4">
          <Trans>
            The User may stop using the Service, delete the Account and cancel the Pro subscription
            at any time.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>8. Cancellation and refunds</Trans>}>
        <Clause n="8.1">
          <Trans>
            The User may cancel the Pro subscription at any time (Art. 32 of the Russian Consumer
            Protection Law). The Provider refunds the unused full days of the billing period at the
            rate of {price} for {period}.
          </Trans>
        </Clause>
        <Clause n="8.2">
          <Trans>
            A refund request is sent to {email} with a link to the Steam profile. The money is
            returned within 10 days of receiving the request, by the same method used for payment
            or, if that is not possible, to the details provided by the User.
          </Trans>
        </Clause>
        <Clause n="8.3">
          <Trans>An erroneous or duplicate charge is refunded in full.</Trans>
        </Clause>
        <Clause n="8.4">
          <Trans>
            If Pro was unavailable through the Provider's fault, the Provider, at the User's choice,
            extends the subscription by that time or refunds those days.
          </Trans>
        </Clause>
        <Clause n="8.5">
          <Trans>
            If access is restricted under clause 7.2, the Provider refunds the unused days of Pro.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>9. Liability</Trans>}>
        <Clause n="9.1">
          <Trans>
            The parties are liable for failure to perform their obligations under the laws of the
            Russian Federation.
          </Trans>
        </Clause>
        <Clause n="9.2">
          <Trans>
            The Provider is not liable for the unavailability of Steam, the game Deadlock, the
            deadlock-api service, the Payment service or banks, or for decisions the User makes in
            the game.
          </Trans>
        </Clause>
        <Clause n="9.3">
          <Trans>
            The parties are released from liability for failure to perform obligations due to force
            majeure.
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>10. User data</Trans>}>
        <Clause n="10.1">
          <Trans>
            The Provider processes the User's data in accordance with the{" "}
            <Link to="/privacy" className={linkClassName}>
              Privacy policy
            </Link>
            .
          </Trans>
        </Clause>
      </LegalSection>

      <LegalSection title={<Trans>11. Term, changes and disputes</Trans>}>
        <Clause n="11.1">
          <Trans>
            The agreement is valid until the Account is deleted. The Pro subscription is valid for
            the paid billing period and is extended by autopay if it is on.
          </Trans>
        </Clause>
        <Clause n="11.2">
          <Trans>
            A new version of the offer is published on this page with the date at the top. The
            version in force on the day of payment applies to a billing period already paid for.
          </Trans>
        </Clause>
        <Clause n="11.3">
          <Trans>
            Claims are sent to {email}; the Provider replies within 10 days. Disputes that cannot be
            settled are resolved under the laws of the Russian Federation in a court determined by
            the jurisdiction rules for consumer protection.
          </Trans>
        </Clause>
      </LegalSection>

      <SellerCard title={<Trans>12. Provider details</Trans>} />
    </LegalDocument>
  );
};
